import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiFileText, FiPrinter, FiSend, FiLink
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { getItemsCached, getCustomersCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import { confirmZeroPriceLines } from '../../utils/zeroPriceLines';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  createTransaction,
  updateTransaction,
  deleteTransaction,
  updateTransactionStatus
} from '../../utils/transactionHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import CustomerSelect from '../../components/CustomerSelect';
import ItemSelect from '../../components/ItemSelect';
import QuickAddItemModal from '../../components/QuickAddItemModal';
import QuickAddCustomerModal from '../../components/QuickAddCustomerModal';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import LinkedDocNumbers from '../../components/LinkedDocNumbers';
import { isFutureDate, checkCustomerCreditLimit } from '../../utils/validationHelpers';
import { getItemStockCached, getRecentItemPrices } from '../../utils/stockFlowHelper';
import ItemPriceHints from '../../components/ItemPriceHints';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine, twoUnitSwitchQty } from '../../utils/unitConversion';
import { loadIfEnabled as loadCustomerPricing, EMPTY_PRICING, filterItemsByPricing, getOverridePrice } from '../../utils/customerPricingHelper';
import { subCategoryLineDiscount } from '../../utils/customerSubCategoryDiscount';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, DiscountInput,
  SortableTh, SortContext
} from '../../components/SharedStyles';

const TRAN_TYPE_CODE = 'QT';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Sent', 'Accepted']
};

const EMPTY_LINE = {
  itemId: '',
  itemCode: '',
  itemName: '',
  description: '',
  quantity: 1,
  unitOfMeasure: '',
  unitCode: '',
  unitPrice: 0,
  priceType: 'Retail',
  discountPercent: 0,
  discountAmount: 0,
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  thirdSchedule: false,
  lineTotal: 0
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Sent: colors.info.main,
  Accepted: colors.success.main,
  Rejected: colors.error.main,
  Expired: colors.neutral[500],
  Cancelled: colors.error.main,
};

const Quotation = () => {
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  const priceClassEnabled = isModuleEnabled && isModuleEnabled('customer_class');
  const thirdScheduleEnabled = isModuleEnabled && isModuleEnabled('sales_third_schedule_tax');
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'quotation');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);


  const location = useLocation();
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: quotations,
    loading,
    refresh: loadQuotations,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingQuotation, setEditingQuotation] = useState(null);
  // Last-4-prices reference shown under each line item (read-only)
  const [priceRefs, setPriceRefs] = useState({});
  const priceReqRef = useRef({});
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    validUntil: '',
    partyId: '',
    partyName: '',
    customerRef: '',
    currency: 'PKR',
    discountAmount: 0,
    notes: '',
    termsConditions: '',
    status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [customerPricing, setCustomerPricing] = useState(EMPTY_PRICING);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stockBalances, setStockBalances] = useState({});

  // Quick-add (create a master record without leaving the quotation).
  // quickAddIndex remembers WHICH line asked for the new item; pendingItemSelect
  // defers selecting it until the new row is actually in `items`, so the normal
  // price/unit auto-fill on the line runs exactly as a manual pick would.
  const [showQuickAddItem, setShowQuickAddItem] = useState(false);
  const [quickAddIndex, setQuickAddIndex] = useState(null);
  const [pendingItemSelect, setPendingItemSelect] = useState(null);
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [pendingCustomerSelect, setPendingCustomerSelect] = useState(null);

  // Quotation list now comes from useDocListPagination above (loadQuotations =
  // its refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): customers/items come from the device-local
  // master cache (getMaster* → IndexedDB, 0 reads per open with a throttled
  // background change-check) instead of a full getDocs on every open. Each screen
  // keeps its own client-side filter/sort on the returned rows.
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getCustomersCached(activeCompany.id);
      setCustomers([...rows].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    } catch (error) { console.error('Error loading customers:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the quotation list itself loads inside useDocListPagination — not here.
  useEffect(() => {
    loadCustomers(); loadItems(); loadEmployees(); loadJobs(); loadLocations();
  }, [loadCustomers, loadItems, loadEmployees, loadJobs, loadLocations]);

  const filteredQuotations = useMemo(() => {
    let filtered = quotations;
    if (statusFilter !== 'All') filtered = filtered.filter(q => q.status === statusFilter);
    if (searchQuery.trim()) {
      const sq = searchQuery.toLowerCase();
      filtered = filtered.filter(q =>
        q.docNumber?.toLowerCase().includes(sq) ||
        q.partyName?.toLowerCase().includes(sq) ||
        q.customerRef?.toLowerCase().includes(sq)
      );
    }
    return sort.sortItems(filtered);
  }, [quotations, statusFilter, searchQuery, sort]);

  // 3rd Schedule mark of a line: the mark saved on the line; lines saved before
  // the mark was kept (no mark at all) take it from the item, like Sale Invoice.
  const lineThirdSchedule = (line) => (typeof line.thirdSchedule === 'boolean'
    ? line.thirdSchedule
    : items.find(i => i.id === line.itemId)?.thirdSchedule === true);

  // Lines opened before the item list arrived (rare): settle their 3rd Schedule
  // mark from the item once the list is here, so the totals use it too.
  useEffect(() => {
    if (!items.length) return;
    setLineItems(prev => (prev.some(l => l.itemId && typeof l.thirdSchedule !== 'boolean')
      ? prev.map(l => (l.itemId && typeof l.thirdSchedule !== 'boolean'
        ? { ...l, thirdSchedule: items.find(i => i.id === l.itemId)?.thirdSchedule === true }
        : l))
      : prev));
  }, [items]);

  const calculateLine = (line) => {
    const amount = (line.quantity || 0) * (line.unitPrice || 0);
    const discAmt = amount * ((line.discountPercent || 0) / 100);
    const net = amount - discAmt;
    // GST base: 3rd Schedule module ON → per item (3rd Schedule = full price
    // before discount, Normal = after discount); module OFF → unchanged (full price).
    const taxBase = thirdScheduleEnabled ? (lineThirdSchedule(line) ? amount : net) : amount;
    const tax = line.taxable ? taxBase * ((line.taxRate || 0) / 100) : 0;
    return {
      ...line,
      discountAmount: Math.round(discAmt * 100) / 100,
      taxAmount: Math.round(tax * 100) / 100,
      lineTotal: Math.round((net + tax) * 100) / 100
    };
  };

  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + ((l.quantity || 0) * (l.unitPrice || 0) - (l.discountAmount || 0)), 0);
    const taxAmount = lines.reduce((s, l) => s + (l.taxAmount || 0), 0);
    const disc = Number(formData.discountAmount) || 0;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      headerDiscount: disc,
      totalAmount: Math.round((subtotal - disc + taxAmount) * 100) / 100
    };
  }, [lineItems, formData.discountAmount]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // A quick-added item lands here: push it into the local list, then let the
  // effect below select it on the line that asked for it once React has it.
  const handleQuickItemCreated = (newItem) => {
    setItems(prev => [...prev, newItem].sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    if (quickAddIndex != null) setPendingItemSelect({ index: quickAddIndex, itemId: newItem.id });
    setShowQuickAddItem(false);
    setQuickAddIndex(null);
  };

  useEffect(() => {
    if (pendingItemSelect && items.some(i => i.id === pendingItemSelect.itemId)) {
      handleLineChange(pendingItemSelect.index, 'itemId', pendingItemSelect.itemId);
      setPendingItemSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingItemSelect]);

  // A quick-added customer is selected straight away. handleCustomerChange reads
  // from `customers`, so the list is updated first and the id passed explicitly.
  const handleQuickCustomerCreated = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    setShowQuickAddCustomer(false);
    setPendingCustomerSelect(newCustomer.id);
  };

  useEffect(() => {
    if (pendingCustomerSelect && customers.some(c => c.id === pendingCustomerSelect)) {
      handleCustomerChange(pendingCustomerSelect);
      setPendingCustomerSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, pendingCustomerSelect]);

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    // Auto-fill the salesperson (employee) from the customer's saved default.
    const spPatch = customer?.salesPersonId
      ? { employeeId: customer.salesPersonId, employeeName: customer.salesPersonName || '' }
      : {};
    setFormData(prev => ({ ...prev, partyId: customerId, partyName: customer?.companyName || '', ...spPatch }));
    if (customerId) {
      loadCustomerPricing(activeCompany?.id, customerId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
  };

  // Load the last-4 SALE prices for each distinct item on the lines (read-only).
  useEffect(() => {
    if (!activeCompany?.id) return;
    const ids = [...new Set(lineItems.map(l => l.itemId).filter(Boolean))];
    ids.forEach(id => {
      if (priceReqRef.current[id]) return;   // fetch once per item per session
      priceReqRef.current[id] = true;
      getRecentItemPrices(activeCompany.id, id, 'sale').then(hints => {
        setPriceRefs(prev => ({ ...prev, [id]: hints || [] }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, activeCompany]);

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Price type change - auto-fill unit price from item
      if (field === 'priceType' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          if (value === 'Retail') updated[index].unitPrice = item.salePrice || 0;
          else if (value === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || 0;
          else if (value === 'Dealer') updated[index].unitPrice = item.dealerPrice || 0;
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
        }
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].itemCode = item.itemCode || '';
          updated[index].itemName = item.itemName || '';
          updated[index].description = item.itemName || '';
          const cust = customers.find(c => c.id === formData.partyId);
          // Customer's fixed discount % (Customers master) seeds the line, same as
          // the Sale Order and Sale Invoice. Blank on the customer → line unchanged.
          if (cust && cust.defaultDiscountPercent != null && cust.defaultDiscountPercent !== '') {
            updated[index].discountPercent = Number(cust.defaultDiscountPercent) || 0;
          }
          // Customer Class Pricing: default the price tier from the customer's class.
          const cls = (priceClassEnabled && cust?.customerClass) || null;
          updated[index].priceType = cls || 'Retail';
          // Sales tax: the customer's own rate (Customers master), when set,
          // OVERRIDES the item's GST — same as the Sale Order and Sale Invoice.
          // Customer 0 = tax-exempt. Blank on the customer → item's own GST.
          const custTax = (cust && cust.defaultTaxRate != null && cust.defaultTaxRate !== '') ? Number(cust.defaultTaxRate) : null;
          if (custTax != null && Number.isFinite(custTax)) {
            updated[index].taxRate = custTax;
            updated[index].taxable = custTax > 0;
          } else {
            updated[index].taxRate = item.gstRate || 0;
            updated[index].taxable = (item.gstRate || 0) > 0;
          }
          updated[index].thirdSchedule = item.thirdSchedule === true;
          // HS Code from the item (carried on to the Sale Order → Challan → Invoice).
          updated[index].hsCode = item.hsCode || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'salePrice', defaultUnitField: 'defaultSaleUnit' });
          updated[index] = { ...updated[index], ...seeded };
          if (cls === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || updated[index].unitPrice;
          else if (cls === 'Dealer') updated[index].unitPrice = item.dealerPrice || updated[index].unitPrice;
          const ovr = getOverridePrice(customerPricing, value);
          if (ovr !== null) updated[index].unitPrice = ovr;
          // Customer Discount by Sub-Category (module): the item's sub-category %
          // for this customer wins; else the default above; else 0 (as Sale Order).
          const scd = subCategoryLineDiscount(cust, item, isModuleEnabled, cust ? cust.defaultDiscountPercent : null);
          if (scd !== undefined) updated[index].discountPercent = scd;
        }
      }

      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'salePrice' });
          updated[index] = { ...updated[index], ...patch };
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
          // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
          // (not on lines linked to another document — their qty is tracked against it).
          const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
            : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].quantity);
          if (twoQ !== null) updated[index].quantity = twoQ;
        }
      }

      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const handlePriceBlur = (index) => {
    const line = lineItems[index];
    if (line.itemId && line.unitPrice > 0) {
      const item = items.find(i => i.id === line.itemId);
      if (item && item.minimumPrice > 0 && line.unitPrice < item.minimumPrice) {
        if (!window.confirm(`Price ${line.unitPrice} is below minimum price ${item.minimumPrice} for ${item.itemName}. Continue?`)) {
          setLineItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], unitPrice: item.minimumPrice };
            updated[index] = calculateLine(updated[index]);
            return updated;
          });
        }
      }
    }
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0 && Number(line.unitPrice) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      validUntil: '', partyId: '', partyName: '', customerRef: '',
      currency: activeCompany?.currencyCode || 'PKR', discountAmount: 0, notes: '', termsConditions: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingQuotation(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };

  const handleEdit = (qt) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingQuotation(qt);
    setFormData({
      docDate: qt.docDate || new Date().toISOString().split('T')[0],
      validUntil: qt.validUntil || '', partyId: qt.partyId || '',
      partyName: qt.partyName || '', customerRef: qt.customerRef || '',
      currency: qt.currency || 'PKR', discountAmount: qt.discountAmount || 0,
      notes: qt.notes || '', termsConditions: qt.termsConditions || '',
      status: qt.status || 'Draft',
      employeeId: qt.employeeId || '', employeeName: qt.employeeName || '',
      jobId: qt.jobId || '', jobName: qt.jobName || '',
      locationId: qt.locationId || '', locationName: qt.locationName || ''
    });
    // Keep the line's own 3rd Schedule mark; an old line without one is resolved
    // from the item (see lineThirdSchedule) instead of defaulting to Normal.
    setLineItems(qt.lines?.length > 0
      ? qt.lines.map(l => ({ ...EMPTY_LINE, ...l, thirdSchedule: typeof l.thirdSchedule === 'boolean' ? l.thirdSchedule : undefined }))
      : [{ ...EMPTY_LINE }]);
    if (qt.partyId) {
      loadCustomerPricing(activeCompany?.id, qt.partyId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
    setViewMode('form');
  };

  const handleView = (qt) => { setViewingQuotation(qt); setViewMode('view'); };

  // Handle navigation from reports (e.g., report drill-down to a document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingQuotation(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Save quotation using transactionHelper
  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a customer'); return; }
    if (!activeFinancialYear?.id) { alert('Please select an active financial year'); return; }

    // Zero price allowed (free items) — negative prices still left out. See utils/zeroPriceLines.js.
    const validLines = lineItems.filter(l => l.description && l.quantity > 0 && Number(l.unitPrice) >= 0);
    if (validLines.length === 0) {
      alert('Please add at least one line item with description, quantity, and price');
      return;
    }
    if (!confirmZeroPriceLines(validLines)) return;

    // Discount cap validation (max 50%)
    for (const line of validLines) {
      if (Number(line.discountPercent) > 50) {
        alert(`Discount on "${line.itemName || line.description}" exceeds 50%. Please review.`); return;
      }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Credit limit check when sending
    if (saveStatus === 'Sent' && formData.partyId) {
      const creditResult = await checkCustomerCreditLimit(activeCompany.id, formData.partyId, totals.totalAmount, editingQuotation?.id);
      if (creditResult.exceeded) {
        if (!window.confirm(`Customer credit limit exceeded!\nLimit: ${creditResult.creditLimit.toLocaleString()}\nOutstanding: ${creditResult.outstanding.toLocaleString()}\nNew total: ${creditResult.newTotal.toLocaleString()}\n\nContinue anyway?`)) return;
      }
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const calc = calculateLine(l);
        return {
          lineNumber: i + 1,
          itemId: l.itemId,
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          description: l.description,
          ...(l.hsCode ? { hsCode: l.hsCode } : {}),
          quantity: l.quantity,
          unitOfMeasure: l.unitOfMeasure || '',
          unitCode: l.unitCode || l.unitOfMeasure || '',
          unitPrice: l.unitPrice,
          discountPercent: l.discountPercent || 0,
          discountAmount: calc.discountAmount,
          taxable: l.taxable,
          taxRate: l.taxRate,
          taxAmount: calc.taxAmount,
          thirdSchedule: lineThirdSchedule(l),
          lineTotal: calc.lineTotal
        };
      });

      const status = saveStatus || formData.status || 'Draft';

      const transactionData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        financialYearId: activeFinancialYear.id,
        partyType: 'customer',
        partyId: formData.partyId,
        partyName: formData.partyName,
        customerRef: formData.customerRef,
        validUntil: formData.validUntil,
        currency: formData.currency,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        subtotal: totals.subtotal,
        discountAmount: totals.headerDiscount,
        taxAmount: totals.taxAmount,
        totalAmount: totals.totalAmount,
        notes: formData.notes,
        termsConditions: formData.termsConditions,
        status,
        lines: calculatedLines
      };

      let newTranData = null;
      let transactionId;

      if (editingQuotation) {
        // Update existing
        await updateTransaction(
          activeCompany.id,
          editingQuotation.id,
          transactionData,
          currentUser.uid
        );

        // Handle status change
        if (status !== editingQuotation.status) {
          await updateTransactionStatus(activeCompany.id, editingQuotation.id, status, currentUser.uid);
        }
        transactionId = editingQuotation.id;
      } else {
        // Create new
        newTranData = await createTransaction(
          activeCompany.id,
          transactionData,
          currentUser.uid
        );

        // Update status if not Draft
        if (status !== 'Draft') {
          await updateTransactionStatus(activeCompany.id, newTranData.id, status, currentUser.uid);
        }
        transactionId = newTranData.id;
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingQuotation
        ? { ...editingQuotation, ...transactionData, id: transactionId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingQuotation
        ? prev.map(r => r.id === transactionId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      Promise.all([loadQuotations()]).catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving quotation:', error);
      alert('Error saving quotation: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (qt) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (qt.childIds && qt.childIds.length > 0) { alert('Cannot delete quotation with linked documents'); return; }
    if (!window.confirm(`Delete Quotation ${qt.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, qt.id, currentUser.uid);
      await loadQuotations();
    } catch (error) { console.error('Error deleting quotation:', error); alert('Could not delete: ' + (error?.message || 'unknown error')); }
  };

  const handlePrint = (qt) => {
    const target = qt || viewingQuotation;
    const cust = customers.find(c => c.id === target?.partyId);
    const enrichedLines = (target?.lines || []).map(l => {
      const it = items.find(i => i.id === l.itemId);
      return { ...l, imageUrl: it?.imageUrl || l.imageUrl || '' };
    });
    const enriched = { ...target, lines: enrichedLines, partyNTN: cust?.ntn || target?.partyNTN || '', partySTRN: cust?.strn || target?.partySTRN || '' };
    printDocument('quotation', enriched, activeCompany, printConfig, activeBranch);
  };

  // Keyboard shortcuts
  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Sent') : undefined,
    viewMode,
    saving
  });

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by quotation#, customer, ref..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Accepted">Accepted</option>
            <option value="Rejected">Rejected</option>
            <option value="Expired">Expired</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Quotation</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading quotations...</LoadingMessage>
      ) : filteredQuotations.length === 0 ? (
        <TranEmptyState>
          <FiFileText size={48} />
          <h3>No Quotations</h3>
          <p>{quotations.length === 0 ? 'Create your first quotation' : 'No quotations match your filters'}</p>
          {quotations.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Quotation</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                <SortableTh field="docNumber">Quotation #</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="validUntil">Valid Until</SortableTh>
                <SortableTh field="partyName">Customer</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Amount</SortableTh>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotations.map(qt => (
                <Tr key={qt.id}>
                  <Td>
                    <strong>{qt.docNumber}</strong>
                    {qt.childIds && qt.childIds.length > 0 && (
                      <LinkedBadge>
                        <FiLink size={10} /> <LinkedDocNumbers ids={qt.childIds} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
                      </LinkedBadge>
                    )}
                  </Td>
                  <Td>{qt.docDate}</Td>
                  {showBranchCol && <Td>{qt.branchName || '-'}</Td>}
                  <Td>{qt.validUntil || '-'}</Td>
                  <Td>{qt.partyName}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {activeCompany?.currencyCode || 'PKR'} {formatAmount(qt.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[qt.status]}>{qt.status}</StatusBadge>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(qt)}><FiEye /></IconBtn>
                      {canChange && (!qt.childIds || qt.childIds.length === 0) && <IconBtn title="Edit" onClick={() => handleEdit(qt)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(qt)}><FiPrinter /></IconBtn>
                      {qt.status !== 'Draft' && qt.status !== 'Cancelled' && (
                        <SendWhatsAppButton templateKey="quotation" doc={qt} party={customers.find(c => c.id === qt.partyId)} variant="icon" />
                      )}
                      {canDelete && (!qt.childIds || qt.childIds.length === 0) && <IconBtn title="Delete" $danger onClick={() => handleDelete(qt)}><FiTrash2 /></IconBtn>}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredQuotations.length : pagination.totalCount}</strong> quotation(s)</span>
        <span>
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Sent: <strong>{listSummary.counts.Sent || 0}</strong> |{' '}
          Accepted: <strong>{listSummary.counts.Accepted || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingQuotation) return null;
    const qt = viewingQuotation;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingQuotation(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {qt.docNumber}
            <StatusBadge $color={STATUS_COLORS[qt.status]} style={{ marginLeft: 12 }}>{qt.status}</StatusBadge>
            {qt.childIds && qt.childIds.length > 0 && (
              <LinkedBadge style={{ marginLeft: 8 }}>
                <FiLink size={12} /> <LinkedDocNumbers ids={qt.childIds} max={0} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
              </LinkedBadge>
            )}
          </FormTitle>
          <FormActions>
            {canChange && (!qt.childIds || qt.childIds.length === 0) && <SaveButton onClick={() => handleEdit(qt)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(qt)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Quotation Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Quotation Number</label><span>{qt.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{qt.docDate}</span></InfoItem>
            <InfoItem><label>Valid Until</label><span>{qt.validUntil || '-'}</span></InfoItem>
            <InfoItem><label>Customer</label><span>{qt.partyName}</span></InfoItem>
            <InfoItem><label>Customer Ref</label><span>{qt.customerRef || '-'}</span></InfoItem>
            <InfoItem><label>Currency</label><span>{activeCompany?.currencyCode || 'PKR'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{qt.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{qt.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{qt.locationName || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>

        <FormCard>
          <SectionTitle>Line Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ textAlign: 'right' }}>Disc %</Th>
                  <Th style={{ textAlign: 'right' }}>Tax</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {(qt.lines || []).map((line, i) => (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? <span style={{ fontSize: '11px', color: '#64748B', marginLeft: '4px' }}>{line.unitOfMeasure}</span> : ''}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.discountPercent || 0}%</Td>
                    <Td style={{ textAlign: 'right' }}>{line.taxable ? formatAmount(line.taxAmount) : '-'}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(qt.subtotal)}</td></tr>
                {qt.discountAmount > 0 && <tr><td>Discount</td><td>-{formatAmount(qt.discountAmount)}</td></tr>}
                <tr><td>Tax</td><td>{formatAmount(qt.taxAmount)}</td></tr>
                <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(qt.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        {qt.termsConditions && <FormCard><SectionTitle>Terms & Conditions</SectionTitle><NotesText>{qt.termsConditions}</NotesText></FormCard>}
        {qt.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{qt.notes}</NotesText></FormCard>}
        <AttachmentPanel transactionId={qt?.id} attachment={qt?.attachment} canEdit={false} />
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
          <FiArrowLeft /> Back to List
        </BackButton>
        <FormTitle>{editingQuotation ? `Edit: ${editingQuotation.docNumber}` : 'New Quotation'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiX /> Cancel
          </SaveButton>
          {canEdit && (
            <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
            </SaveButton>
          )}
          {canEdit && (formData.status === 'Draft' || !editingQuotation) && (
            <SaveButton $approve onClick={() => handleSave('Sent')} disabled={saving}>
              <FiSend /> Save & Send
            </SaveButton>
          )}
        </FormActions>
      </FormHeader>

      <FormCard>
        <SectionTitle>Quotation Details</SectionTitle>
        <FormGrid>
          <FormGroup>
            <Label>Quotation Number</Label>
            <Input value={editingQuotation?.docNumber || 'Auto-generated'} disabled />
          </FormGroup>
          <FormGroup>
            <Label>Quotation Date *</Label>
            <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Valid Until</Label>
            <Input type="date" value={formData.validUntil} onChange={e => handleHeaderChange('validUntil', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Status</Label>
            <Input value={formData.status} disabled />
          </FormGroup>
          <FormGroup $span2>
            <Label>Customer *</Label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CustomerSelect customers={customers} selectedCustomerId={formData.partyId} onChange={handleCustomerChange} balanceOnDemand />
              </div>
              <button type="button" title="Quick add new customer"
                onClick={() => setShowQuickAddCustomer(true)}
                style={{ flexShrink: 0, width: 26, height: 26, border: '1px solid #ccc', borderRadius: 6, background: '#fff', color: '#800020', fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}>+</button>
            </div>
          </FormGroup>
          <FormGroup>
            <Label>Customer Ref</Label>
            <Input value={formData.customerRef} onChange={e => handleHeaderChange('customerRef', e.target.value)} placeholder="Customer inquiry ref" />
          </FormGroup>
          <FormGroup>
            <Label>Currency</Label>
            <Input value={activeCompany?.currencyCode || 'PKR'} disabled />
          </FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => {
              const emp = employees.find(em => em.id === e.target.value);
              handleHeaderChange('employeeId', e.target.value);
              handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
            }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job</Label>
            <Select value={formData.jobId} onChange={e => {
              const job = jobs.find(j => j.id === e.target.value);
              handleHeaderChange('jobId', e.target.value);
              handleHeaderChange('jobName', job?.jobName || '');
            }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{reqLabel(activeCompany, 'Location')}</Label>
            <Select value={formData.locationId} onChange={e => {
              const loc = locations.find(l => l.id === e.target.value);
              handleHeaderChange('locationId', e.target.value);
              handleHeaderChange('locationName', loc?.locationName || '');
            }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>

      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
          <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
        </LineItemsHeader>

        <LineTableContainer>
          <LineTable>
            <thead>
              <tr>
                <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
                <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
                {lineItems.some(l => l.hsCode) && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>HS Code</LineTh>}
                <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
                <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Price Type</LineTh>
                <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh>
                <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Disc %</LineTh>
                <LineTh style={{ width: 80, minWidth: 80, maxWidth: 80 }}>Tax?</LineTh>
                <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Tax %</LineTh>
                <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
                <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((line, index) => {
                const calc = calculateLine(line);
                return (
                  <tr key={index}>
                    <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                    <LineTd>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={filterItemsByPricing(items, customerPricing)} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} />
                        </div>
                        <button type="button" title="Quick add new item"
                          onClick={() => { setQuickAddIndex(index); setShowQuickAddItem(true); }}
                          style={{ flexShrink: 0, width: 26, height: 26, border: '1px solid #ccc', borderRadius: 6, background: '#fff', color: '#800020', fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}>+</button>
                      </div>
                      {line.itemId && <ItemPriceHints hints={priceRefs[line.itemId]} />}
                    </LineTd>
                    <LineTd>
                      <LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" />
                    </LineTd>
                    {lineItems.some(l => l.hsCode) && (
                      <LineTd>
                        <LineInput value={line.hsCode || ''} onChange={e => handleLineChange(index, 'hsCode', e.target.value)} placeholder="HS Code" />
                      </LineTd>
                    )}
                    <LineTd>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} />
                        <LineUnitPicker
                          item={items.find(i => i.id === line.itemId)}
                          line={line}
                          onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                        />
                      </div>
                      <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                        quantity={line.quantity} hasSize={line.hasSize}
                        onChange={(q) => handleLineChange(index, 'quantity', q)} />
                    </LineTd>
                    <LineTd>
                      <Select value={line.priceType || 'Retail'} onChange={e => handleLineChange(index, 'priceType', e.target.value)} style={{ padding: '4px 6px', fontSize: '12px', minWidth: 80 }}>
                        <option value="Retail">Retail</option>
                        <option value="Wholesale">Wholesale</option>
                        <option value="Dealer">Dealer</option>
                      </Select>
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" step="0.01" value={line.unitPrice} onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))} onBlur={() => handlePriceBlur(index)} style={{ textAlign: 'right' }} />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" max="100" value={line.discountPercent} onChange={e => handleLineChange(index, 'discountPercent', Number(e.target.value))} style={{ textAlign: 'right' }} />
                    </LineTd>
                    <LineTd style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={line.taxable}
                        onChange={e => handleLineChange(index, 'taxable', e.target.checked)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !line.taxable) handleLastFieldEnter(e, index); }} />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" max="100" value={line.taxRate}
                        onChange={e => handleLineChange(index, 'taxRate', Number(e.target.value))}
                        onKeyDown={(e) => handleLastFieldEnter(e, index)}
                        disabled={!line.taxable} style={{ textAlign: 'right', opacity: line.taxable ? 1 : 0.4 }} />
                    </LineTd>
                    <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                    <LineTd style={{ textAlign: 'center' }}>
                      {lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>}
                    </LineTd>
                  </tr>
                );
              })}
            </tbody>
          </LineTable>
        </LineTableContainer>

        <TotalsSection>
          <TotalsTable>
            <tbody>
              <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
              <tr>
                <td>Discount</td>
                <td><DiscountInput type="number" min="0" step="0.01" value={formData.discountAmount} onChange={e => handleHeaderChange('discountAmount', Number(e.target.value))} /></td>
              </tr>
              <tr><td>Tax</td><td>{formatAmount(totals.taxAmount)}</td></tr>
              <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalAmount)}</td></TotalRow>
            </tbody>
          </TotalsTable>
        </TotalsSection>
      </FormCard>

      <FormCard>
        <SectionTitle>Additional Information</SectionTitle>
        <FormGrid>
          <FormGroup $span2>
            <Label>Terms & Conditions</Label>
            <TextArea value={formData.termsConditions} onChange={e => handleHeaderChange('termsConditions', e.target.value)} placeholder="Terms and conditions..." rows={3} />
          </FormGroup>
          <FormGroup $span2>
            <Label>Notes</Label>
            <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes..." rows={3} />
          </FormGroup>
        </FormGrid>
      </FormCard>

      <AttachmentPanel
        transactionId={editingQuotation?.id}
        attachment={editingQuotation?.attachment}
        canEdit={canEdit}
        onChange={({ attachment }) => setEditingQuotation(prev => prev ? { ...prev, attachment } : prev)}
      />
    </>
  );

  return (
    <PageLayout pageTitle="Quotation" breadcrumb="Sales / Quotation">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
      {showQuickAddItem && (
        <QuickAddItemModal
          companyId={activeCompany?.id}
          existingItems={items}
          onCreated={handleQuickItemCreated}
          onClose={() => { setShowQuickAddItem(false); setQuickAddIndex(null); }}
        />
      )}
      {showQuickAddCustomer && (
        <QuickAddCustomerModal
          companyId={activeCompany?.id}
          onCreated={handleQuickCustomerCreated}
          onClose={() => setShowQuickAddCustomer(false)}
        />
      )}
    </PageLayout>
  );
};

// ============ UNIQUE STYLED COMPONENTS ============
const LinkedBadge = styled.span`display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:500;color:${colors.info.main};background:${colors.info.main}15;margin-left:6px;`;

export default Quotation;
